import { useEffect } from 'react';
import type { Staff } from './api';

const editable = (t: EventTarget | null) => t instanceof HTMLElement && (t.closest('input, textarea, [contenteditable="true"]') !== null);

/**
 * Client data stays in the CRM: staff (everyone except the owner) can read it, but not copy,
 * cut, drag, print or save it. A faint watermark with their name ties any photo of the screen back to them.
 * Typing and pasting into forms still works.
 */
export function useDataGuard(staff: Staff) {
  const on = staff.role !== 'owner';
  useEffect(() => {
    document.documentElement.classList.toggle('guarded', on);
    if (!on) return;
    const stop = (e: Event) => { if (!editable(e.target)) e.preventDefault(); };
    const keys = (e: KeyboardEvent) => {
      const k = e.key.toLowerCase();
      if ((e.ctrlKey || e.metaKey) && (k === 'p' || k === 's' || ((k === 'c' || k === 'x' || k === 'a') && !editable(e.target)))) e.preventDefault();
    };
    const evs: [string, EventListener][] = [['copy', stop], ['cut', stop], ['contextmenu', stop], ['dragstart', stop], ['selectstart', stop], ['keydown', keys as EventListener]];
    evs.forEach(([n, f]) => document.addEventListener(n, f, true));
    return () => evs.forEach(([n, f]) => document.removeEventListener(n, f, true));
  }, [on]);
  return on;
}

export function Watermark({ staff }: { staff: Staff }) {
  const text = `${staff.name} · ${staff.email} · ${new Date().toLocaleDateString()}`;
  const svg = `<svg xmlns='http://www.w3.org/2000/svg' width='420' height='220'><text x='10' y='120' transform='rotate(-24 210 110)' font-family='sans-serif' font-size='14' fill='rgba(0,0,0,0.045)'>${text.replace(/[<&>']/g, '')}</text></svg>`;
  return <div className="watermark" aria-hidden="true" style={{ backgroundImage: `url("data:image/svg+xml,${encodeURIComponent(svg)}")` }} />;
}
